import { useEventBus } from '@vueuse/core';
import { reactive, ref, type Ref } from 'vue';
import { Assert } from '../../assert';

export type EntityChanged<T> = EntityUpdated<T> | EntityRemoved;

export interface EntityUpdated<T> {
  id: string;
  action: 'updated';
  data: T;
}

export interface EntityRemoved {
  id: string;
  action: 'removed';
}

export interface EntityStoreOptions<T> {
  eventName: string;
  getEntityId: (entity: T) => string;
  updateEntity: (dest: T, src: T) => void;
  insertEntity?: (entity: T) => void;
  beforeUpdateEntity?: (entity: T) => void;
  afterUpdateEntity?: (entity: T) => void;
  removeEntity?: (entity: T) => void;
}

export function useEntityStore<T extends object>(options: EntityStoreOptions<T>) {
  const {
    getEntityId,
    insertEntity,
    updateEntity,
    removeEntity,
    beforeUpdateEntity,
    afterUpdateEntity,
  } = options;

  const list = ref<T[]>([]) as Ref<T[]>;
  const byId = new Map<string, T>();
  const changed = useEventBus<EntityChanged<T>>(options.eventName);

  function getById(id: string) {
    return byId.get(id);
  }

  function getOrThrow(id: string) {
    const entity = getById(id);
    Assert.definied(entity);
    return entity;
  }

  function add(entity: T): 'inserted' | 'updated' {
    const entityId = getEntityId(entity);

    const existing = getById(entityId);

    if (!existing) {
      const newEntity = reactive(entity) as T;
      list.value.push(newEntity);
      byId.set(entityId, newEntity);
      if (insertEntity) {
        insertEntity(newEntity);
      }
      changed.emit({
        id: entityId,
        action: 'updated',
        data: newEntity,
      });
      return 'inserted';
    } else {
      update(existing, (existing) => updateEntity(existing, entity));
      return 'updated';
    }
  }

  function update(entity: T, action: (entity: T) => void) {
    if (beforeUpdateEntity) {
      beforeUpdateEntity(entity);
    }

    action(entity);

    if (afterUpdateEntity) {
      afterUpdateEntity(entity);
    }

    const id = getEntityId(entity);

    changed.emit({
      id: id,
      action: 'updated',
      data: entity,
    });
  }

  function remove(id: string) {
    const index = list.value.findIndex((e) => getEntityId(e) === id);
    if (index < 0) return false;

    const entity = list.value[index];
    if (!entity) return false;

    list.value.splice(index, 1);
    byId.delete(id);
    if (removeEntity) {
      removeEntity(entity);
    }
    changed.emit({
      id: id,
      action: 'removed',
    });
    return true;
  }

  function merge(entities: T[]) {
    const ids = new Set<string>();

    for (const entity of entities) {
      add(entity);
      ids.add(getEntityId(entity));
    }

    for (const entity of list.value) {
      const id = getEntityId(entity);
      if (!ids.has(id)) {
        remove(id);
      }
    }
  }

  function clear() {
    const ids = list.value.map((e) => getEntityId(e));
    list.value.length = 0;
    byId.clear();

    for (const productId of ids) {
      changed.emit({
        id: productId,
        action: 'removed',
      });
    }

    return ids;
  }

  return {
    getById,
    add,
    remove,
    merge,
    clear,
    getOrThrow,
    update,
    list,
    changed,
  };
}
